import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, Check, Copy, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { daysLeft, pct, rp } from "@/pages/GalangDanaPage";

const QRIS_URL = "https://customer-assets-agu9un31.emergentagent.net/job_alibtisam-portal/artifacts/9y9zgxhb_QRIS%20Al%20Ibtisam.jpeg";
const BRI_ACCOUNT = "5922-01-063805-53-5";

export default function GalangDanaDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const payRef = useRef(null);

  useEffect(() => {
    api.get(`/campaigns/${id}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="campaign-not-found">
        <p className="text-slate-500">404</p>
        <Link to="/galang-dana" className="text-emerald-800 font-semibold hover:underline">{t("common.back")}</Link>
      </div>
    );
  }
  if (!c) return <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500">{t("common.loading")}</div>;

  const sisa = daysLeft(c.deadline);
  const steps = lang === "id"
    ? ['Klik tombol "Donasi Sekarang"', "Salin nomor rekening BRI atau pindai kode QRIS", "Transfer sesuai nominal donasi Anda", "Konfirmasi donasi melalui WhatsApp admin"]
    : ['Click the "Donate Now" button', "Copy the BRI account number or scan the QRIS code", "Transfer your donation amount", "Confirm your donation via the admin WhatsApp"];

  const copyAccount = async () => {
    try {
      await navigator.clipboard.writeText(BRI_ACCOUNT.replaceAll("-", ""));
      setCopied(true);
      toast.success(t("galang.tersalin"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(BRI_ACCOUNT);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="galang-dana-detail-page">
      <Link to="/galang-dana" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-[#D4AF37] transition-colors mb-6" data-testid="campaign-back-link">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Konten utama */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-2">
          {c.image && <img src={imgUrl(c.image)} alt={c.title} className="w-full aspect-video object-cover rounded-xl border border-emerald-900/10" />}
          {c.category && (
            <span className="inline-block mt-6 text-[11px] font-bold uppercase tracking-widest text-[#D4AF37]">{c.category}</span>
          )}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-emerald-950 tracking-tight leading-tight mt-2">{c.title}</h1>

          <h2 className="font-display text-2xl font-bold text-emerald-900 mt-10 mb-4">{t("galang.tentang")}</h2>
          <div className="prose-content text-slate-700 text-base leading-relaxed">
            {(c.description || "").split("\n\n").filter(Boolean).map((par, i) => <p key={i}>{par}</p>)}
          </div>

          <h2 className="font-display text-2xl font-bold text-emerald-900 mt-10 mb-4">{t("galang.cara")}</h2>
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-emerald-800 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-sm text-slate-700">{s}</span>
              </li>
            ))}
          </ol>

          {/* Pembayaran */}
          <div ref={payRef} className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10" data-testid="payment-section">
            <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-5 w-5 text-emerald-800" />
                <h3 className="font-semibold text-emerald-950">{t("galang.transfer")}</h3>
              </div>
              <p className="text-xs text-slate-500">Bank BRI — a.n. Al Ibtisam Kudus</p>
              <p className="font-mono text-lg font-bold text-emerald-900 mt-1.5 tracking-wide" data-testid="bri-account">{BRI_ACCOUNT}</p>
              <div className="flex gap-2 mt-4">
                <Button onClick={copyAccount} variant="outline" className="rounded-full border-emerald-800 text-emerald-800 flex-1" data-testid="copy-account-button">
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />} {t("galang.salin")}
                </Button>
                <Button asChild className="rounded-full bg-emerald-800 hover:bg-emerald-900 flex-1">
                  <a href="https://wa.me/6285117711353" target="_blank" rel="noopener noreferrer" data-testid="confirm-wa-button">{t("galang.konfirmasi")}</a>
                </Button>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <QrCode className="h-5 w-5 text-emerald-800" />
                <h3 className="font-semibold text-emerald-950">{t("galang.qris")}</h3>
              </div>
              <a href={QRIS_URL} target="_blank" rel="noopener noreferrer">
                <img src={QRIS_URL} alt="QRIS Al Ibtisam" className="w-full max-w-[240px] mx-auto rounded-lg border border-slate-200" data-testid="qris-image" />
              </a>
              <p className="text-xs text-slate-400 mt-3">DONASI AL IBTISAM KUDUS · NMID: ID1026505602460</p>
            </div>
          </div>
        </motion.div>

        {/* Panel donasi sticky */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <div className="lg:sticky lg:top-24 bg-white rounded-xl border border-emerald-900/10 shadow-sm p-7" data-testid="donation-panel">
            <p className="text-xs uppercase tracking-wide text-slate-400">{t("galang.terkumpul")}</p>
            <p className="font-display text-3xl font-bold text-emerald-800 mt-1">{rp(c.collected)}</p>
            <div className="h-2.5 rounded-full bg-emerald-100 overflow-hidden mt-4">
              <div className="h-2.5 rounded-full bg-[#D4AF37]" style={{ width: `${pct(c)}%` }} />
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>{pct(c)}% {t("galang.tercapai")}</span>
              {c.target > 0 && <span>{t("galang.target")}: {rp(c.target)}</span>}
            </div>
            {sisa !== null && (
              <div className="mt-5 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-900/10 px-4 py-3">
                <span className="text-sm text-slate-600">{t("galang.sisa")}</span>
                <span className="font-display text-2xl font-bold text-emerald-900">{sisa}</span>
              </div>
            )}
            <Button onClick={() => payRef.current?.scrollIntoView({ behavior: "smooth" })} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 h-12 text-base mt-6" data-testid="donate-now-button">
              {t("galang.donasi")}
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
